import React from 'react';
import { formatCurrency } from 'Core/utils/numberFormat';
import ModernRadioOption from 'Core/components/ModernOption/ModernRadioOption';
import RichTextDescriptionItem from 'Core/components/RichTextDescriptionItem';
import { TextButton } from 'Core/components/Button';
import { FaCheckCircle } from 'react-icons/fa';
import { ImBlocked } from 'react-icons/im';
import classnames from 'classnames';

export default function OfferOptions({
  offers,
  selectedOffer,
  setSelectedOffer,
  amount_to_pay,
  already_paid_amount,
  showOfferDetails,
}) {
  return offers.installment.map(({ terms, type, rich_text_descriptions }, idx) => {
    const richTextDescriptions = rich_text_descriptions || [];
    const [
      {
        id,
        term: fixedTerm,
        apr: autoPayApr,
        apr_max: regularApr,
        amount_min,
        require_downpayment_percent,
        promo: { monthly_premium = '', term: promoTerm = '' } = {},
      },
    ] = terms;
    const isPromo = promoTerm && monthly_premium;
    const checked = selectedOffer.id === id;
    const isDisabled = amount_to_pay - already_paid_amount < amount_min;

    return (
      <div
        className="!flex-col-12 min-h-[15em] first:pl-0 last:pr-0 px-2 mb-4 lg:!flex-col-4"
        key={id}
      >
        <ModernRadioOption
          key={id}
          className="mb-3"
          name="offer"
          value={id}
          checked={checked}
          disabled={isDisabled}
          variant={isPromo ? 'success' : 'branding'}
          data-testid={'select-offer-radio-' + idx}
          onChange={(e) => {
            setSelectedOffer({
              id: e.target.value,
              amount_min,
              require_downpayment_percent,
              type,
            });
          }}
        >
          <div className="flex flex-col justify-around radio-header h-128">
            <section
              className={classnames('flex justify-between  px-2 py-4 items-center', {
                'bg-gray-300': !isPromo && !checked,
                'bg-blue-200': !isPromo && checked,
                'bg-patientfi-bg': isPromo,
              })}
            >
              <div className="text-center font-bold text-xl px-2 leading-tight text-gray-600">
                {fixedTerm + ' Months'}
              </div>
              <div className="text-center font-semibold text-sm px-2 tracking-wide">
                {isPromo ? (
                  <div className="rounded-full bg-gray-200 text-teal-600 px-2 py-2 font-bold">
                    {promoTerm + ' Month promo'}
                  </div>
                ) : (
                  <div className="rounded-full bg-gray-200 text-gray-500 px-2 py-2">Fixed term</div>
                )}
              </div>
            </section>

            {isPromo && (
              <section
                className={classnames(
                  'border flex gap-2 justify-between border-slate-300 rounded-xl p-3 m-2 items-center flex-wrap'
                )}
              >
                <section className="w-1/2">
                  <div className="font-normal text-sm mb-1 text-gray-500">
                    Suggested Payment
                    <span className="text-sm text-red-500 pl-1">{isPromo ? '*' : ''}</span>
                  </div>
                  <div className="font-bold text-2xl tracking-tight">
                    {formatCurrency(monthly_premium, { withDecimals: true })}
                  </div>
                </section>
                <section className="text-right">
                  <div className="font-normal text-sm text-gray-500">PROMO APR</div>
                  <div className="font-bold text-lg tracking-tight text-slate-500">0%</div>
                </section>
              </section>
            )}

            {isPromo && <div className="w-full my-1 text-center text-gray-500">OR</div>}

            <div className="border flex gap-2 justify-between border-slate-300 rounded-xl p-3 m-2 items-center flex-wrap">
              <section>
                <div className="font-normal text-sm text-gray-500 mb-1">
                  Minimum Monthly
                  <span className="text-sm text-red-500 pl-1">{isPromo ? '*' : ''}</span>
                </div>
                <div className="font-bold text-2xl tracking-tight">
                  {formatCurrency(amount_min, { withDecimals: true })}
                </div>
              </section>

              {!isPromo && (
                <section className="text-right">
                  <div className="font-normal text-sm text-gray-500">FIXED APR</div>
                  <div className="font-bold text-lg tracking-tight text-slate-500">
                    {regularApr}%
                  </div>
                  <div className=" text-sm text-gray-500 tracking-tight">
                    {autoPayApr}% with Autopay
                  </div>
                </section>
              )}

              {isPromo && (
                <section className="text-right">
                  <div className="font-normal text-sm text-gray-500">POST PROMO APR</div>
                  <div className="font-bold text-lg tracking-tight text-slate-500">
                    {regularApr}%
                  </div>
                  <div className=" text-sm text-gray-500 tracking-tight">
                    {autoPayApr}% with Autopay
                  </div>
                </section>
              )}
            </div>

            {!checked ? (
              !isDisabled ? (
                !isPromo ? (
                  <div className="mt-28 pt-14"></div>
                ) : (
                  <div className="mt-5 pt-4"></div>
                )
              ) : (
                <div className="mb-3 mt-auto text-gray-400 flex items-center justify-center text-base">
                  <span className="inline-block">
                    <ImBlocked />
                  </span>
                  <span className="ml-2 font-medium">Not Eligible</span>
                </div>
              )
            ) : (
              <TextButton
                className={classnames(
                  'pointer-events-none flex items-center justify-center  relative bottom-0',
                  { 'mt-16 mb-16': !isPromo }
                )}
              >
                <FaCheckCircle />
                <div className="ml-2 text-base font-medium">Selected</div>
              </TextButton>
            )}
          </div>

          {isPromo && (
            <div className="text-xs leading-relaxed px-3 pb-1 mt-2 text-gray-400">
              <span className="text-xs text-red-500">*</span>Final monthly payment can be selected
              before the final step.
            </div>
          )}

          {showOfferDetails && richTextDescriptions?.length && (
            <div className="h-full px-4 radio-description text-sm flex flex-col justify-start pt-4">
              <ul>
                {richTextDescriptions.map((item) => {
                  return (
                    <RichTextDescriptionItem
                      key={item.id}
                      id={item.id}
                      iconName={item.icon_name}
                      title={item.title}
                      text={item.text}
                    />
                  );
                })}
              </ul>
            </div>
          )}
        </ModernRadioOption>
      </div>
    );
  });
}
